// @flow

import moment from "moment"

export const TRIP_STATUS = {
  PENDING: "PENDING",
  EN_ROUTE: "EN_ROUTE",
  IN_VEHICLE: "IN_VEHICLE",
  DROPPED_OFF: "DROPPED_OFF",
  NO_SHOW: "NO_SHOW",
  INVALID_NO_SHOW: "INVALID_NO_SHOW",
  CANCELLED: "CANCELLED",
  CANCELLED_AT_DOOR: "CANCELLED_AT_DOOR",
  COMPLETED: "COMPLETED",
  UNKNOWN: "UNKNOWN",
  FAILED: "FAILED",
  EXTERNAL: "EXTERNAL"
}

export const PAYMENT_METHOD_TYPE = {
  CREDIT_CARD: "CREDIT_CARD",
  EXTERNAL: "EXTERNAL",
  INVOICE: "INVOICE",
  PAY_IN_CAR: "PAY_IN_CAR",
  PAY_PAL: "PAY_PAL",
  TRAVEL_ACCOUNT: "TRAVEL_ACCOUNT"
}

export type TripStatus = $Keys<typeof TRIP_STATUS>

export type Price = {
  amount: string,
  currency: string
}

export type Traveler = {
  id: string,
  name: string,
  phoneNumber: string,
  comment: string
}

export type Booker = {
  id: string,
  firstName: string,
  lastName: string,
  phoneNumber: string
}

export type Driver = {
  id: string,
  firstName: string,
  lastName: string,
  phoneNumber: string
}

export type Address = {
  id: string,
  type: string,
  street?: string,
  streetNumber?: string,
  placeName?: string,
  latitude: number,
  longitude: number,
  zipCode: string,
  city: string,
  countryCode: string
}

export type Product = {
  productId: string,
  subProductId: string,
  optionIds: Array<String>
}

export type Eta = {
  timeMinutes: number,
  type?: "BEFORE" | "AFTER" | "NO_DIFFERENCE"
}

export type OrderEvent = {
  type: string,
  receivedAt: string,
  completedAt: string,
  success: boolean,
  brand: string,
  client: string,
  appVersion: string,
  vehicleRegNo: string,
  driverLicenseId: string,
  fleetName: string,
  bookingAgent: string,
  handledUser: string,
  noShowType: string,
  skipCarQuotaCheck: boolean,
  nextPickupTime: string,
  cancellationReason: Object,
  etaDifference: Eta,
  etaCarArrival: Eta,
  pickupTimeDifference: Eta,
  sourceSystem: string
}

export type Vehicle = {
  countryCode: string,
  registrationNumber: string,
  taxiNumber: string,
  manufacturer: string,
  model: string
}

export type PaymentMethod = {
  id: string,
  paymentMethodType: typeof PAYMENT_METHOD_TYPE
}

export type PaymentTransaction = {
  eventType: string,
  transactionId: string,
  date: string
}

export type Payment = {
  paymentMethod: PaymentMethod,
  transactions: Array<PaymentTransaction>,
  paymentReferences: Set<string, string>
}

export type Voucher = {
  id: string,
  amount: Price
}

export type ProductPrice = {
  id: string,
  price: Price
}

export type DiscountOrFee = {
  id: string,
  amount: string
}

export type EconomyContent = {
  fixedPrice: string,
  customerPrice: Price,
  vatPercentage: string,
  vatAmount: Price,
  discountId: string,
  basePrice: string,
  productPrice: Array<ProductPrice>,
  voucher: Array<Voucher>,
  discounts: Array<DiscountOrFee>,
  totalDiscount: number,
  fees: Array<DiscountOrFee>,
  totalFees: number
}

export type ExternalReference = {
  provider: string,
  id: string
}

export type Fleet = {
  id: number,
  displayName: string
}

export type RatingCategoryComment = {
  da: string,
  en: string,
  fi: string,
  nb: string,
  sv: string
}

export type RatingCategory = {
  id: number,
  name: RatingCategoryComment,
  created: string,
  categoryOrder: number
}

export type Rating = {
  id: number,
  orderId: string,
  publicOrderId: string,
  rating: number,
  categories: Array<RatingCategory>,
  created: string
}

export type Order = {
  orderId: string,
  publicId: string,
  state: TripStatus,
  travelerPhoneNumber: string,
  travelerName: number,
  fromAddress: Address,
  toAddress: Address,
  viaAddress: Array<Address>,
  pickupTime: string,
  customerPrice: string,
  fixedPrice: boolean,
  currencyCode: string,
  source: string,
  travelNow: boolean,
  externalReferences: Array<ExternalReference>,
  fleetId: string,
  status: string,
  cancellable: boolean,
  canHandle: boolean,
  timeRemainingToHandle: typeof moment,
  createdAt: string
}

export type DetailedOrder = {
  id: string,
  publicId: string,
  externalReference: Object,
  createdAt: string,
  state: TripStatus,
  booker: Booker,
  traveler: Traveler,
  driver: Driver,
  fromAddress: Address,
  viaAddress: Array<Address>,
  toAddress: Address,
  pickupTime: string,
  timezoneId: string,
  brandId: string,
  vehicle: Vehicle,
  economyContent: EconomyContent,
  product: Product,
  orderEvents: Array<OrderEvent>,
  payment: Payment,
  shouldEnableManualHandle: boolean,
  cancellable: boolean,
  modifiable: boolean,
  fleet: Fleet,
  rating: Rating,
  travelNow: boolean
}

export type VehicleOnMapDetails = {
  metersToCustomer: number,
  secondsToCustomer: number,
  vehicleNumber: string,
  vehicleStatus: string,
  position: {
    latitude: number,
    longitude: number
  }
}
